/**
 * Shrinks a phone photo in the browser before it is uploaded.
 *
 * WHY: a receipt photo straight off a phone camera is 3–8 MB. The API runs
 * behind a Lambda whose request payload is capped at 6 MB, and the photo
 * travels as base64 inside JSON, which inflates it by a third — so an
 * untouched photo routinely fails with a 413 on an airport connection that
 * already took ages to push it up. Re-encoding to a 1600px JPEG keeps a
 * receipt perfectly legible at roughly 200–600 KB.
 *
 * HOW:
 *   1. decode the file through an <img> (object URL). Modern browsers apply
 *      the EXIF orientation there, so a portrait photo stays upright on the
 *      canvas — createImageBitmap is less consistent about that.
 *   2. scale so the LONGEST edge is at most `maxEdge`; never upscale.
 *   3. paint white, then draw: JPEG has no alpha, and a transparent PNG
 *      screenshot would otherwise come out black.
 *   4. `canvas.toDataURL('image/jpeg', quality)` and drop the `data:…;base64,`
 *      prefix — the API wants bare base64.
 */

const MAX_EDGE = 1600;
const JPEG_QUALITY = 0.8;

// Base64 characters. Leaves comfortable room under the 6 MB Lambda ceiling for
// the JSON envelope; a 1600px JPEG is nowhere near this, so hitting it means
// something is badly wrong with the image rather than that it needs retrying.
const MAX_BASE64_LENGTH = 4 * 1024 * 1024;

class ImageProcessingError extends Error {
  constructor(message, reason) {
    super(message);
    this.name = 'ImageProcessingError';
    this.reason = reason;
  }
}

const loadImage = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const image = new Image();
  image.onload = () => {
    URL.revokeObjectURL(url);
    resolve(image);
  };
  image.onerror = () => {
    URL.revokeObjectURL(url);
    reject(new ImageProcessingError('The photo could not be decoded', 'decode'));
  };
  image.src = url;
});

/** 'IMG_4411.HEIC' -> 'IMG_4411.jpg'; the bytes are JPEG after re-encoding. */
export const jpegName = (name) => {
  const base = String(name || '').replace(/\.[^./\\]+$/, '').trim() || 'blokk';
  return `${base}.jpg`;
};

/** Target size for a `width`×`height` image so the longest edge ≤ `maxEdge`. */
export const fitWithin = (width, height, maxEdge = MAX_EDGE) => {
  const longest = Math.max(width, height);
  const scale = longest > maxEdge ? maxEdge / longest : 1;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
};

/**
 * Resolves to `{ name, type, data, width, height }` where `data` is bare
 * base64 JPEG (no `data:` prefix). Rejects with ImageProcessingError when the
 * file is not an image the browser can decode.
 */
export async function downscaleImage(file, { maxEdge = MAX_EDGE, quality = JPEG_QUALITY } = {}) {
  if (!file) throw new ImageProcessingError('No file', 'missing');

  const image = await loadImage(file);
  const sourceWidth = image.naturalWidth || image.width;
  const sourceHeight = image.naturalHeight || image.height;
  if (!sourceWidth || !sourceHeight) {
    throw new ImageProcessingError('The photo has no dimensions', 'decode');
  }

  const { width, height } = fitWithin(sourceWidth, sourceHeight, maxEdge);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new ImageProcessingError('Canvas is unavailable', 'canvas');

  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, width, height);
  context.drawImage(image, 0, 0, width, height);

  const dataUrl = canvas.toDataURL('image/jpeg', quality);
  const comma = dataUrl.indexOf(',');
  const data = comma >= 0 ? dataUrl.slice(comma + 1) : '';
  if (!data) throw new ImageProcessingError('Encoding failed', 'encode');
  if (data.length > MAX_BASE64_LENGTH) {
    throw new ImageProcessingError('Encoded photo is still too large', 'too_large');
  }

  // Let the browser drop the backing store now rather than at GC time: phones
  // with little memory feel a lingering 1600px canvas.
  canvas.width = 0;
  canvas.height = 0;

  return {
    name: jpegName(file.name),
    type: 'image/jpeg',
    data,
    width,
    height,
  };
}
